// ============================================
// useDistributorProgress — Calcula barras + regalos del programa de distribuidoras
// ============================================

import { useEffect, useMemo, useState } from 'react';
import { useCustomer } from '../context/CustomerContext';
import {
  getDistributorTiers,
  getMyDistributorStatus,
} from '../services/medusa/distributor';

// Mínimo de pedido distribuidor en MXN (alineado con DISTRIBUTOR_MIN_CART_TOTAL_MAJOR del backend).
export const DISTRIBUTOR_MIN_CART_TOTAL = 20000;

export function useDistributorProgress(subtotal = 0) {
  const { token } = useCustomer() || {};
  const [isDistributor, setIsDistributor] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [statusRes, tiersRes] = await Promise.all([
          token ? getMyDistributorStatus(token).catch(() => null) : Promise.resolve(null),
          getDistributorTiers().catch(() => ({ tiers: [] })),
        ]);
        if (cancelled) return;
        setIsDistributor(Boolean(statusRes?.is_distributor));
        const sorted = (tiersRes?.tiers || [])
          .slice()
          .sort((a, b) => Number(a.min_amount) - Number(b.min_amount));
        setTiers(sorted);
      } catch {
        if (!cancelled) setTiers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return useMemo(() => {
    const safe = Number(subtotal) || 0;
    const minTotal = DISTRIBUTOR_MIN_CART_TOTAL;
    const reachedMin = safe >= minTotal;
    const missingToMin = Math.max(0, minTotal - safe);
    const minProgress = Math.min(1, safe / minTotal);

    const achievedTiers = tiers.filter((t) => safe >= Number(t.min_amount));
    const nextTier = tiers.find((t) => safe < Number(t.min_amount)) || null;
    const missingToNext = nextTier
      ? Math.max(0, Number(nextTier.min_amount) - safe)
      : 0;
    const nextTierProgress = nextTier
      ? Math.min(1, safe / Number(nextTier.min_amount))
      : 1;

    return {
      isDistributor,
      loading,
      minTotal,
      reachedMin,
      missingToMin,
      minProgress,
      tiers,
      achievedTiers,
      nextTier,
      missingToNext,
      nextTierProgress,
      checkoutBlocked: isDistributor && !reachedMin,
    };
  }, [isDistributor, loading, tiers, subtotal]);
}
